import {
  PlaneWorkspace,
  PlaneProject,
  PlaneIssue,
  PlaneCycle,
  PlaneModule,
  PlaneState,
  PlaneLabel,
  PlanePaginatedResponse,
  PlaneApiError,
} from './types'

// Environment variables for Plane.so configuration
// Use proxy in development to avoid CORS issues
const isDev = import.meta.env.DEV
const PLANE_API_URL = isDev ? '/plane-api' : (import.meta.env.VITE_PLANE_API_URL || '')
const PLANE_API_KEY = import.meta.env.VITE_PLANE_API_KEY || ''
const PLANE_WORKSPACE_SLUG = import.meta.env.VITE_PLANE_WORKSPACE_SLUG || 'nexus'

interface PlaneClientConfig {
  apiUrl?: string
  apiKey?: string
  workspaceSlug?: string
}

class PlaneClient {
  private apiUrl: string
  private apiKey: string
  private workspaceSlug: string

  constructor(config: PlaneClientConfig = {}) {
    this.apiUrl = config.apiUrl || PLANE_API_URL
    this.apiKey = config.apiKey || PLANE_API_KEY
    this.workspaceSlug = config.workspaceSlug || PLANE_WORKSPACE_SLUG
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ data: T | null; error: PlaneApiError | null }> {
    if (!this.apiUrl || !this.apiKey) {
      return {
        data: null,
        error: { error: 'Plane.so not configured', detail: 'Missing API URL or API Key' },
      }
    }

    try {
      const url = `${this.apiUrl}${endpoint}`
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey,
          ...options.headers,
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return {
          data: null,
          error: {
            error: `HTTP ${response.status}`,
            detail: errorData.detail || errorData.error || response.statusText,
          },
        }
      }

      // Handle empty responses (e.g., 204 No Content from DELETE)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return { data: null as T, error: null }
      }

      // Try to parse JSON, but handle empty body gracefully
      const text = await response.text()
      if (!text) {
        return { data: null as T, error: null }
      }

      const data = JSON.parse(text)
      return { data, error: null }
    } catch (err: any) {
      return {
        data: null,
        error: { error: 'Network error', detail: err.message },
      }
    }
  }

  // Check if Plane.so is configured
  isConfigured(): boolean {
    return !!(this.apiUrl && this.apiKey)
  }

  // ============ Workspace Methods ============

  async getWorkspaces(): Promise<{ data: PlaneWorkspace[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneWorkspace[]>('/api/v1/workspaces/')
  }

  async getWorkspace(slug: string = this.workspaceSlug): Promise<{ data: PlaneWorkspace | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneWorkspace>(`/api/v1/workspaces/${slug}/`)
  }

  // ============ Project Methods ============

  async getProjects(workspaceSlug: string = this.workspaceSlug): Promise<{
    data: PlaneProject[] | null
    error: PlaneApiError | null
  }> {
    return this.fetch<PlaneProject[]>(`/api/v1/workspaces/${workspaceSlug}/projects/`)
  }

  async getProject(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneProject | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneProject>(`/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/`)
  }

  async createProject(
    data: Partial<PlaneProject>,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneProject | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneProject>(`/api/v1/workspaces/${workspaceSlug}/projects/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // ============ Issue Methods ============

  async getIssues(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug,
    params?: {
      state?: string
      priority?: string
      assignees?: string
      labels?: string
      cursor?: string
      per_page?: number
    }
  ): Promise<{ data: PlanePaginatedResponse<PlaneIssue> | null; error: PlaneApiError | null }> {
    const searchParams = new URLSearchParams()
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) searchParams.set(key, String(value))
      })
    }
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return this.fetch<PlanePaginatedResponse<PlaneIssue>>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${query}`
    )
  }

  async getIssue(
    projectId: string,
    issueId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneIssue | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneIssue>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/`
    )
  }

  async createIssue(
    projectId: string,
    data: Partial<PlaneIssue>,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneIssue | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneIssue>(`/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateIssue(
    projectId: string,
    issueId: string,
    data: Partial<PlaneIssue>,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneIssue | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneIssue>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  }

  async deleteIssue(
    projectId: string,
    issueId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: null; error: PlaneApiError | null }> {
    return this.fetch<null>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/`,
      { method: 'DELETE' }
    )
  }

  // ============ Cycle Methods ============

  async getCycles(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneCycle[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneCycle[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/cycles/`
    )
  }

  async getCycle(
    projectId: string,
    cycleId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneCycle | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneCycle>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/cycles/${cycleId}/`
    )
  }

  async getCycleIssues(
    projectId: string,
    cycleId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneIssue[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneIssue[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/cycles/${cycleId}/cycle-issues/`
    )
  }

  // ============ Module Methods ============

  async getModules(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneModule[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneModule[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/modules/`
    )
  }

  async getModule(
    projectId: string,
    moduleId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneModule | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneModule>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/modules/${moduleId}/`
    )
  }

  // ============ State & Label Methods ============

  async getStates(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneState[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneState[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/states/`
    )
  }

  async getLabels(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: PlaneLabel[] | null; error: PlaneApiError | null }> {
    return this.fetch<PlaneLabel[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/labels/`
    )
  }

  // ============ Member Methods ============

  async getProjectMembers(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: any[] | null; error: PlaneApiError | null }> {
    return this.fetch<any[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/members/`
    )
  }

  async getWorkspaceMembers(
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: any[] | null; error: PlaneApiError | null }> {
    return this.fetch<any[]>(
      `/api/v1/workspaces/${workspaceSlug}/members/`
    )
  }

  // ============ Comment Methods ============

  async getIssueComments(
    projectId: string,
    issueId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: any[] | null; error: PlaneApiError | null }> {
    return this.fetch<any[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/comments/`
    )
  }

  async createIssueComment(
    projectId: string,
    issueId: string,
    comment: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: any | null; error: PlaneApiError | null }> {
    return this.fetch<any>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/comments/`,
      {
        method: 'POST',
        body: JSON.stringify({ comment_html: `<p>${comment}</p>` }),
      }
    )
  }

  async getIssueActivity(
    projectId: string,
    issueId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{ data: any[] | null; error: PlaneApiError | null }> {
    return this.fetch<any[]>(
      `/api/v1/workspaces/${workspaceSlug}/projects/${projectId}/issues/${issueId}/activities/`
    )
  }

  // ============ Aggregation Methods ============

  async getProjectMetrics(
    projectId: string,
    workspaceSlug: string = this.workspaceSlug
  ): Promise<{
    data: { total: number; completed: number; open: number; overdue: number } | null
    error: PlaneApiError | null
  }> {
    const { data: issues, error } = await this.getIssues(projectId, workspaceSlug, { per_page: 1 })
    if (error) return { data: null, error }

    // This is a simplified version - in production you'd use Plane's analytics endpoints
    return {
      data: {
        total: issues?.count || 0,
        completed: 0, // Would need to filter by state
        open: 0,
        overdue: 0,
      },
      error: null,
    }
  }

  async getAllProjectsIssueCount(workspaceSlug: string = this.workspaceSlug): Promise<{
    data: Record<string, number> | null
    error: PlaneApiError | null
  }> {
    const { data: projects, error } = await this.getProjects(workspaceSlug)
    if (error || !projects) return { data: null, error }

    const counts: Record<string, number> = {}
    for (const project of projects) {
      const { data: issues } = await this.getIssues(project.id, workspaceSlug, { per_page: 1 })
      counts[project.id] = issues?.count || 0
    }

    return { data: counts, error: null }
  }
}

// Singleton instance
let planeClient: PlaneClient | null = null

export function getPlaneClient(config?: PlaneClientConfig): PlaneClient {
  if (!planeClient || config) {
    planeClient = new PlaneClient(config)
  }
  return planeClient
}

export { PlaneClient }
